import React, { useMemo } from 'react';
import { JSONValidator, WrapperEntitySelector, ExternalLink } from '@components/Inputs';
import { getCSVDownloadHardLimit } from '@gral/config';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Grid, TextInput, Typography, CronInput } from '@ts/storybook';
import UserRoles, { defaultConfigComponent as URConfigComponent } from '../Users/UserRoles';

import Error404 from '@components/Errors/404';
import { useAppSelector } from '@redux/store';
import DefaultLanguageForm from './defaultLanguageForm';
import MobileAppSettingsForm from './MobileAppSettingsForm';
import UnitSysteForm from './UnitSystemForm';
import AllowTsEmailsForm from './AllowEmailsForm';
import { useLocation } from 'react-router';
import { IForm } from '@ts/ui-models';
import ColourSetForm from './ParameterOptions/ColourSetForm';

interface IParameterForm extends IForm {}

const ParameterForm = (props: IParameterForm) => {
  const { mode, values: parameter, onCallbackChange } = props;
  const user = useAppSelector(state => state.global.user);
  const location = useLocation();
  const locationState = location.state ?? {};
  const { openInJSON } = locationState;
  const { parameterType } = parameter ?? {};
  const isViewMode = mode === 'view';

  //#region Handlers
  const _handleChangeNewEntitySelector = (entity, entityName, fkField) => {
    const value = entity ? entity.getID() : null;
    onCallbackChange(entityName)(entity);
    return onCallbackChange(fkField)(value);
  };

  const _handleHomeScreen = value => {
    if (value.charAt(0) == '/') value = value.substring(1);
    onCallbackChange('value.homeScreen')(value);
  };

  //#endregion

  //#region Render
  const _renderLogoForm = () => {
    return React.createElement(
      'div',
      null,
      <TextInput isReadOnly label="Parameter type" value={parameter.name} onChangeValue={onCallbackChange('name')} />,
      <TextInput
        isReadOnly={isViewMode}
        label="Notes"
        value={parameter.notes}
        onChangeValue={onCallbackChange('notes')}
        multiline
      />,
      <WrapperEntitySelector
        label={'Logo'}
        value={parameter.logo ?? parameter.value.logoId}
        searchComponent={generateSearchEntitySelector('document')}
        onChangeEntity={entity => _handleChangeNewEntitySelector(entity, 'logo', 'value.logoId')}
      />,
      <ExternalLink
        linkLabel={'URL'}
        captionLabel={'Caption'}
        onChangeLink={onCallbackChange('value.logoUrl')}
        onChangeCaption={onCallbackChange('value.logoCaption')}
        url={parameter.value.logoUrl}
        caption={parameter.value.logoCaption}
        fDisabled={isViewMode}
        viewFields={mode === 'edit' || mode === 'new'}
      />
    );
  };

  const _renderMenuForm = () => {
    const onChangeMenuList = jsonValue => {
      if (typeof jsonValue === 'string') jsonValue = JSON.parse(jsonValue);
      if (Array.isArray(jsonValue)) {
        const seenIds = new Set();
        jsonValue = jsonValue.filter(obj => {
          if (!obj) return true;
          if (obj.id && seenIds.has(obj.id)) {
            delete obj.id;
          }

          if (obj.id) {
            seenIds.add(obj.id);
          }

          if (obj.route && !obj.route.startsWith('/')) {
            obj.route = '/' + obj.route;
          }

          return true;
        });
      }
      onCallbackChange('value.menu')(jsonValue);
    };

    return (
      <div>
        <JSONValidator
          label={'Menu in JSON format'}
          value={parameter.value.menu}
          placeholder={[{ route: '/home', name: 'Home', icon: 'home', component: 'route' }]}
          disabled={isViewMode}
          onJSONValidated={onChangeMenuList}
        />
      </div>
    );
  };

  const _renderDataGridConfigForm = () => {
    const onChangeDataGridConfig = jsonValue => {
      onCallbackChange('value')(jsonValue);
    };

    return (
      <div>
        <JSONValidator
          label={'Data grid config in JSON format'}
          placeholder={{}}
          value={parameter.value}
          disabled={isViewMode}
          onJSONValidated={onChangeDataGridConfig}
        />
      </div>
    );
  };

  const _renderColourSetForm = () => {
    return <ColourSetForm parameter={parameter} isViewMode={isViewMode} onCallbackChange={onCallbackChange} />;
  };

  const _renderDefaultViewForm = () => {
    return (
      <WrapperEntitySelector
        label={'Default view'}
        searchComponent={generateSearchEntitySelector('view')}
        value={parameter.value.defaultViewId}
        onChangeEntity={entity => _handleChangeNewEntitySelector(entity, 'defaultView', 'value.defaultViewId')}
        isReadOnly={isViewMode}
      />
    );
  };

  const _renderDefaultMobileAppViewForm = () => {
    return (
      <WrapperEntitySelector
        label={'Default mobile app view'}
        searchComponent={generateSearchEntitySelector('view')}
        value={parameter.value.defaultMobileAppViewId}
        onChangeEntity={entity => _handleChangeNewEntitySelector(entity, 'defaultMobileAppView', 'value.defaultMobileAppViewId')}
        isReadOnly={isViewMode}
      />
    );
  };

  const _renderStatusIndicatorsDownstreamTransactionModeForm = () => {
    return (
      <JSONValidator
        label={'Status indicators downstream transaction mode'}
        value={parameter.value.statusIndicatorsDownstreamTransactionMode}
        disabled={isViewMode}
        onJSONValidated={onCallbackChange('value.statusIndicatorsDownstreamTransactionMode')}
        placeholder={[
          {
            statusIndicator: 'Battery Voltage',
            mode: 'uponChangeOfValue',
          },
        ]}
      />
    );
  };

  const _renderHomeScreenForm = () => {
    return (
      <TextInput
        disabled={isViewMode}
        label="Home screen route must be an existing id or a custom route"
        value={parameter.value.homeScreen}
        onChangeValue={_handleHomeScreen}
      />
    );
  };

  const _renderDefaultLanguageForm = () => {
    return <DefaultLanguageForm {...props} />;
  };

  const _renderUnitSystem = () => {
    return <UnitSysteForm {...props} />;
  };

  const _renderMobileAppSettingsForm = () => {
    return <MobileAppSettingsForm {...props} />;
  };

  const _renderPredefinedFilterForm = () => {
    return (
      <JSONValidator
        label={'Predefined filter'}
        placeholder={'Predefined filter in JSON format'}
        value={parameter.value.predefinedFilter}
        onJSONValidated={onCallbackChange('value.predefinedFilter')}
      />
    );
  };

  const _renderAllowTSEmails = () => {
    return <AllowTsEmailsForm {...props} />;
  };

  const _renderMaximumSeverity = () => {
    return (
      <Grid container spacing={1} direction={'column'}>
        <Typography variant="body1" text="Maximum Severity" />
        <TextInput
          type="number"
          value={parameter.value.maximumSeverity}
          onChangeValue={v => {
            const value = Math.abs(Math.trunc(v as number));
            onCallbackChange('value.maximumSeverity')(value);
          }}
          min={0}
          max={15}
        />
      </Grid>
    );
  };

  const _renderMinimumSeverity = () => {
    return (
      <Grid container spacing={1} direction={'column'}>
        <Typography variant="body1" text="Minimum Severity" />
        <TextInput
          type="number"
          value={parameter.value.minimumSeverity}
          onChangeValue={v => {
            const value = Math.abs(Math.trunc(v as number));
            onCallbackChange('value.minimumSeverity')(value);
          }}
        />
      </Grid>
    );
  };

  const _renderCSVDownloadLimit = () => {
    return (
      <div>
        <TextInput
          type="number"
          value={parameter.value.CSVDownloadLimit}
          onChangeValue={v => {
            const value = Math.abs(Math.trunc(v as number));
            onCallbackChange('value.CSVDownloadLimit')(value);
          }}
        />
        <p>{`The maximum number of records when downloading a CSV file will be limited by this setting.
               In no case the limit will exceed ${getCSVDownloadHardLimit()} records.
            `}</p>
      </div>
    );
  };

  const _renderDownstreamTimeout = () => {
    return (
      <div>
        <TextInput
          type="number"
          value={parameter.value.downstreamSnapshotCreationTimeout}
          onChangeValue={v => {
            const value = Math.abs(Math.trunc(v as number));
            onCallbackChange('value.downstreamSnapshotCreationTimeout')(value);
          }}
        />
        <Typography variant="body1" text="Interval in seconds between the creation of snapshots" />
      </div>
    );
  };

  const _renderCustomSetting = () => {
    console.log('parameter.value', parameter.value);
    return (
      <Grid container spacing={16}>
        <Grid size={{ xs: 12 }}>
          <TextInput
            isReadOnly={isViewMode}
            label="Setting"
            value={parameter.value?.key}
            onChangeValue={onCallbackChange('value.key')}
          />
          <JSONValidator
            label={'Value'}
            disabled={isViewMode}
            value={parameter.value?.value}
            onJSONValidated={onCallbackChange('value.value')}
          />
        </Grid>
      </Grid>
    );
  };

  const _renderMobileSnapshotCreationSchedule = () => {
    return <CronInput value={parameter.value.schedule} onChange={onCallbackChange('value.schedule')} />;
  };

  const _renderLoginPreferences = () => {
    const organizationId = parameter.value.defaultOrganization ?? null;

    const searchComponentConfig = useMemo(() => {
      return {
        component: UserRoles,
        configComponent: {
          ...URConfigComponent,
          fetchURL: `/api/v2/user/${user?.id}/organization/${organizationId}/roles`,
        },
      };
    }, [user, organizationId]);

    return (
      <>
        <WrapperEntitySelector
          label="Organization"
          value={organizationId}
          onChangeEntity={entity => _handleChangeNewEntitySelector(entity, 'organization', 'value.defaultOrganization')}
          searchComponent={generateSearchEntitySelector('organization')}
          disabled={isViewMode}
        />
        {organizationId && (
          <WrapperEntitySelector
            label="Role"
            value={parameter.value.defaultRole}
            onChangeEntity={entity => _handleChangeNewEntitySelector(entity, 'role', 'value.defaultRole')}
            searchComponent={searchComponentConfig}
            disabled={isViewMode}
            verificationFetch="/api/roles"
          />
        )}
      </>
    );
  };

  const renderJSONValidator = (label: string, v: any) => (
    <JSONValidator label={label} value={v} disabled={isViewMode} onJSONValidated={onCallbackChange('value')} />
  );

  //#endregion

  if (openInJSON) {
    return renderJSONValidator('JSON ' + parameterType.form, parameter.value);
  }

  switch (parameterType?.form) {
    case 'logo':
      return _renderLogoForm();
    case 'menu':
      return _renderMenuForm();
    case 'dataGridConfig':
      return _renderDataGridConfigForm();
    case 'colourSet':
      return _renderColourSetForm();
    case 'defaultView':
      return _renderDefaultViewForm();
    case 'defaultMobileAppView':
      return _renderDefaultMobileAppViewForm();
    case 'mobileAppSettings':
      return _renderMobileAppSettingsForm();
    case 'predefinedFilter':
      return _renderPredefinedFilterForm();
    case 'homeScreen':
      return _renderHomeScreenForm();
    case 'allowTSEmails':
      return _renderAllowTSEmails();
    case 'defaultLanguage':
      return _renderDefaultLanguageForm();
    case 'maximumSeverity':
      return _renderMaximumSeverity();
    case 'minimumSeverity':
      return _renderMinimumSeverity();
    case 'CSVDownloadLimit':
      return _renderCSVDownloadLimit();
    case 'downstreamSnapshotCreationTimeout':
      return _renderDownstreamTimeout();
    case 'customSetting':
      return _renderCustomSetting();
    case 'mobileSnapshotCreationSchedule':
      return _renderMobileSnapshotCreationSchedule();
    case 'loginPreferences':
      return _renderLoginPreferences();
    case 'unitSystem':
      return _renderUnitSystem();
    case 'statusIndicatorsDownstreamTransactionMode':
      return _renderStatusIndicatorsDownstreamTransactionModeForm();
    default:
      return <Error404 message={'Parameter not found'} />;
  }
};

export default ParameterForm;
